import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaClock, FaMapMarkerAlt } from "react-icons/fa";
import { PiCalendarStarLight, PiShoppingCartDuotone } from "react-icons/pi";
import api from "../helpers/Api";
import Loading from "../components/Loading";
import { SkeletonHomeEvents } from "../components/Skeleton";
import moment from "moment-timezone";

export function Render({ eventsData }) {
    const [activeTab, setActiveTab] = useState(null);
    const navigate = useNavigate();

    const URL_ORIGIN = process.env.NODE_ENV === "development"
        ? process.env.REACT_APP_API_URL_DEV
        : process.env.REACT_APP_API_URL;

    const routeChange = (eventId) => {
        navigate(`/event/detail/${eventId}`);
    };

    const eventsByMonth = useMemo(() => {
        if (!eventsData) return {};
        return eventsData.reduce((acc, event) => {
            const dateObj = new Date(event.event_date);
            const monthYear = dateObj.toLocaleString("es-ES", {
                month: "long",
                year: "numeric",
            });
            if (!acc[monthYear]) acc[monthYear] = [];
            acc[monthYear].push(event);
            return acc;
        }, {});
    }, [eventsData]);

    useEffect(() => {
        const sortedMonths = Object.keys(eventsByMonth).sort((a, b) => {
            const dateA = new Date(eventsByMonth[a][0].event_date);
            const dateB = new Date(eventsByMonth[b][0].event_date);
            return dateA - dateB;
        });
        if (sortedMonths.length > 0) {
            setActiveTab(sortedMonths[0]);
        }
    }, [eventsByMonth]);

    if (!eventsData || eventsData.length === 0) {
        return (
            <div className="bg-[#0f0e17] text-white py-16 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <span className="text-brand font-semibold text-sm uppercase tracking-widest font-mono">Cartelera</span>
                    <h1 className="text-3xl font-bold mt-2 text-gray-300 font-display">No hay eventos disponibles por el momento</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0f0e17] text-white py-14 px-4">
            <div className="max-w-7xl mx-auto">

                <div className="text-center mb-10">
                    <span className="text-brand font-semibold text-sm uppercase tracking-widest font-mono">Cartelera</span>
                    <h2 className="text-white font-bold text-3xl sm:text-4xl mt-2 font-display">Próximos Eventos</h2>
                </div>

                <div className="schedule-one__main-tab-box tabs-box mb-8">
                    <div className="tab-buttons clearfix list-unstyled content-center items-center flex flex-wrap gap-3 justify-center">
                        {Object.keys(eventsByMonth)
                            .sort((a, b) => {
                                const dateA = new Date(eventsByMonth[a][0].event_date);
                                const dateB = new Date(eventsByMonth[b][0].event_date);
                                return dateA - dateB;
                            })
                            .map((month) => (
                                <button
                                    key={month}
                                    onClick={() => setActiveTab(month)}
                                    className={`tab-btn flex items-center gap-2 whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-semibold border transition-all duration-200 ${activeTab === month
                                            ? "bg-brand border-brand text-white shadow-lg shadow-brand/20"
                                            : "bg-white/5 border-white/10 text-gray-300 hover:border-brand/50 hover:text-white"
                                        }`}
                                >
                                    <PiCalendarStarLight size={18} />
                                    {month.charAt(0).toUpperCase() + month.slice(1)}
                                </button>
                            ))}
                    </div>
                </div>

                <div className="space-y-4">
                    {activeTab &&
                        eventsByMonth[activeTab]?.map((event) => {
                            const dateFormatted = new Date(event.event_date).toLocaleDateString("es-ES", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            });
                            const timeFormatted = moment(event.event_date).format("hh:mm a");

                            const primaryImage = event.event_images?.find(img => img.is_primary);

                            return (
                                <div
                                    key={event.event_id}
                                    className="group flex flex-col sm:flex-row bg-[#16151c] rounded-2xl overflow-hidden border border-white/10 hover:border-brand/30 transition-all duration-300 shadow-xl"
                                >
                                    {primaryImage && (
                                        <div className="sm:w-44 md:w-52 h-48 sm:h-auto flex-shrink-0 relative overflow-hidden">
                                            <img
                                                src={`${URL_ORIGIN}/image-proxy?id=${primaryImage.id}`}
                                                alt={primaryImage.image_name || event.event_name}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#16151c]/50 hidden sm:block" />
                                        </div>
                                    )}

                                    <div className="flex-1 p-5 flex flex-col justify-between gap-4">
                                        <div>
                                            <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5 font-display">{event.event_name}</h3>
                                            <p className="text-gray-400 text-sm line-clamp-2 leading-relaxed">{event.event_description}</p>
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            <span className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 border border-white/10 text-gray-300 text-xs">
                                                <FaClock className="text-brand" />
                                                {timeFormatted}
                                            </span>
                                            <span className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 border border-white/10 text-gray-300 text-xs capitalize">
                                                <FaCalendarAlt className="text-brand" />
                                                {dateFormatted}
                                            </span>
                                            <span className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 border border-white/10 text-gray-300 text-xs">
                                                <FaMapMarkerAlt className="text-brand" />
                                                <span className="truncate max-w-[180px] sm:max-w-xs">{event.event_location}</span>
                                            </span>
                                        </div>

                                        <div>
                                            <button
                                                className="event-one__btn thm-btn"
                                                onClick={() => routeChange(event.event_id)}
                                            >
                                                <span className="flex items-center gap-2">
                                                    <PiShoppingCartDuotone size={20} />
                                                    Comprar boletos
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
};

export default function Event() {
    const [event, setEvent] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const eventsResponse = await api.get(`/Event/All`)
                setEvent(eventsResponse.data);
            } catch (error) {
                console.error("Error fetching event data:", error);
            }
        };

        fetchData();
    }, []);

    if (!event) return <SkeletonHomeEvents />;

    return <Render eventsData={event} />;
}
